// Small display helpers shared by the At-Home admin and teacher pages.

export const fmtTime = (hhmmss) => {
  if (!hhmmss) return ''
  const [h, m] = hhmmss.split(':').map(Number)
  const suffix = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`
}

export const fmtDate = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })
}

export const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit'
      })
    : ''

// A Date as YYYY-MM-DD in the browser's local zone. Never toISOString() for
// this: that is UTC, which in India is still yesterday until 5:30 am.
export const isoLocal = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`
    : ''

export const todayLocal = () => isoLocal(new Date())

// True once a slot's start time has passed (local clock), so a slot earlier
// today is "past", not "upcoming" (owner, 2026-10-13). Falls back to the
// date alone when the time is missing.
export const slotHasStarted = (date, startTime) => {
  if (!date) return false
  const [y, m, d] = date.split('-').map(Number)
  const [hh, mm] = (startTime || '00:00').split(':').map(Number)
  return new Date(y, m - 1, d, hh || 0, mm || 0) <= new Date()
}

export const fmtMoney = (n) =>
  n === null || n === undefined || n === ''
    ? ''
    : `₹${Number(n).toLocaleString('en-IN')}`

export const titleCase = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : ''

// The fields management asked for on every At-Home table, in their order:
// Category, Teacher, Duration, Grade, Date, Time, Venue, Trial, Session Price.
// Every column carries its own header dropdown (AtHomeCrudPage); dates and
// times keep the API's order in that dropdown instead of A→Z.
export const SESSION_COLUMNS = [
  { name: 'categoryName', label: 'Category' },
  { name: 'teacherName', label: 'Teacher' },
  { name: 'duration', label: 'Duration' },
  { name: 'gradesLabel', label: 'Grade' },
  { name: 'dateLabel', label: 'Date', options: { keepOrder: true } },
  { name: 'timeLabel', label: 'Time', options: { keepOrder: true } },
  { name: 'apartmentName', label: 'Apartment' },
  { name: 'kindLabel', label: 'Session Type' },
  { name: 'priceLabel', label: 'Price' }
]

/**
 * The classes a session is for, listed out: "1,2,3,4,5,6,7,8".
 *
 * EVERY CLASS IS NAMED, never summarised (owner, 2026-10-20). The field is a
 * multi-select, so the column shows what is ticked in it -- not "Any", which
 * named no classes at all, and not "1-8", which is a range the form cannot
 * express and which hides whether, say, 4 is actually among them.
 *
 * Commas without spaces: eight values have to fit a table cell, and
 * "1, 2, 3, 4, 5, 6, 7, 8" is half again as wide for nothing.
 *
 * `grade` IS NOT CHANGED to match, and must not be. It is the wildcard the
 * parent app keys on -- `catalogue/types.ts` exports `ANY_GRADE = 'Any'` and
 * both booking sheets use it to decide which times to grey out -- so a slot
 * relabelled in the database would be read there as a class no child is in,
 * and every slot would grey out for every parent. This is a column, not a
 * migration.
 *
 * A row the backfill has not reached has no list: 'Any' and blank both mean
 * every class, so they are expanded rather than shown as themselves, and a
 * single stored class is shown as itself.
 */
export const gradesLabelOf = (s) => {
  const picked = Array.isArray(s.grades) ? s.grades.map(String) : []
  if (picked.length) return picked.join(',')
  const old = (s.grade || '').trim()
  return !old || old === 'Any' ? GRADES.join(',') : old
}

// Flatten a slot (admin/slots) into table cells.
export const slotRow = (s) => ({
  ...s,
  gradesLabel: gradesLabelOf(s),
  sessionName: s.session_name || '',
  linksOut: s.pendingLinks
    ? `${s.pendingLinks} link${s.pendingLinks === 1 ? '' : 's'} out`
    : '',
  duration: `${s.duration_minutes} min`,
  dateLabel: fmtDate(s.date),
  timeLabel: `${fmtTime(s.start_time)} – ${fmtTime(s.endTime)}`,
  kindLabel: s.kind === 'trial' ? 'Trial' : 'Regular',
  priceLabel: fmtMoney(s.price),
  statusLabel: titleCase(s.status)
})

const localDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

// Whole days between the tap's calendar day and the session's day.
const daysAfter = (tap, date) =>
  Math.round(
    (new Date(tap.getFullYear(), tap.getMonth(), tap.getDate()) -
      localDate(date)) /
      86400000
  )

// How a teacher's tap relates to the scheduled time, as a full timestamp
// (owner, 2026-10-12: a bare clock time was not friendly):
//   "Fri, 9 Oct, 5:04 pm (4 min late)"   ·  "Fri, 9 Oct, 4:55 pm (5 min early)"
//   "Fri, 9 Oct, 5:00 pm (on time)"      ·  "Sun, 11 Oct, 11:31 am (2 days later)"
//   "—"                                  never marked
// `against` is the scheduled HH:MM to compare with (start for Started, end
// for Completed); `words` are the late/early labels. Gaps over an hour read
// as "11 h 32 min".
const span = (mins) =>
  mins >= 60
    ? `${Math.floor(mins / 60)} h${mins % 60 ? ` ${mins % 60} min` : ''}`
    : `${mins} min`

const stamp = (iso, date, against, words = ['late', 'early']) => {
  if (!iso || !date) return '—'
  const tap = new Date(iso)
  const when = fmtDateTime(iso)
  const days = daysAfter(tap, date)
  if (days !== 0) {
    const rel =
      days > 0
        ? `${days} day${days === 1 ? '' : 's'} later`
        : `${-days} day${days === -1 ? '' : 's'} before`
    return `${when} (${rel})`
  }
  if (!against) return when
  const [hh, mm] = against.split(':').map(Number)
  const sched = localDate(date)
  sched.setHours(hh, mm, 0, 0)
  const diff = Math.round((tap - sched) / 60000)
  if (Math.abs(diff) <= 2) return `${when} (on time)`
  return `${when} (${span(Math.abs(diff))} ${diff > 0 ? words[0] : words[1]})`
}

// The three quick-reply buttons on the feedback message, as the parent saw
// them (whatsapp/webhook.py FEEDBACK_REPLIES stores the codes).
export const FEEDBACK_LABELS = {
  loved_it: 'Loved it',
  okay: 'It was okay',
  not_great: 'Not great'
}

// " (53 min after the session ended)" -- or "before", for a session closed
// early -- from the scheduled end; '' when the end is unknown.
const afterEnd = (iso, date, endTime) => {
  if (!iso || !date || !endTime) return ''
  const [hh, mm] = endTime.split(':').map(Number)
  const end = localDate(date)
  end.setHours(hh, mm, 0, 0)
  const diff = Math.round((new Date(iso) - end) / 60000)
  if (diff === 0) return ' (as the session ended)'
  return ` (${span(Math.abs(diff))} ${
    diff > 0 ? 'after' : 'before'
  } the session ended)`
}

// When a booking was made -- not when its session is -- as the two cells the
// Bookings tabs lead with (owner, 2026-10-21).
const bookedCells = (iso) => ({
  bookedOnLabel: iso ? fmtDate(isoLocal(new Date(iso))) : '',
  bookedAtLabel: iso
    ? new Date(iso).toLocaleTimeString('en-IN', {
        hour: 'numeric',
        minute: '2-digit'
      })
    : ''
})

// Flatten a booking (admin/bookings, teacher/sessions) into table cells.
export const bookingRow = (b) => ({
  ...b,
  rescheduledLabel: b.reschedules?.length
    ? `${b.reschedules.length}× (was ${fmtDate(b.reschedules[0].fromDate)})`
    : '—',
  duration: `${b.durationMinutes} min`,
  dateLabel: fmtDate(b.date),
  timeLabel: `${fmtTime(b.startTime)} – ${fmtTime(b.endTime)}`,
  kindLabel: b.kind === 'trial' ? 'Trial' : 'Regular',
  sessionLabel: b.sessionLabel || '',
  // "Session 3 of 8 · Plant waterer" — the concept is dropped in tables so rows stay one line.
  sessionShort: (b.sessionLabel || '').split(' · ').slice(0, 2).join(' · '),
  moduleLabel: b.moduleName || '',
  priceLabel: fmtMoney(b.payment_amount),
  childName: b.childInfo?.name || '',
  // The class the parent picked while booking (kept on the child's record),
  // else their school standard's own name when At Home has no grade for it.
  childGradeLabel: b.childInfo?.grade || b.childInfo?.standardName || '—',
  schoolName: b.childInfo?.schoolName || '—',
  parentName: b.parentInfo?.name || '',
  parentPhone: b.parentInfo?.phone || '',
  parentUuid: b.parentInfo?.uuid || '',
  ...bookedCells(b.created),
  paymentLabel:
    b.payment_status === 'link_sent'
      ? b.plan
        ? 'Link sent · with the project'
        : 'Link sent · not booked until paid'
      : titleCase(b.payment_status),
  sourceLabel: b.source === 'parent_app' ? 'Parent app' : 'Alfred',
  statusLabel: titleCase(b.status),
  startedLabel: stamp(b.report?.started_at, b.date, b.startTime),
  completedLabel: stamp(b.report?.completed_at, b.date, b.endTime, [
    'over',
    'early'
  ]),
  markedBy: b.report?.markedBy?.name || '',
  attendanceLabel:
    b.report?.attended === true
      ? 'Attended'
      : b.report?.attended === false
      ? 'Not Attended'
      : 'Not marked',
  // The parent's quick reply to the WhatsApp feedback message, in the
  // button's own words, and when it came in.
  feedbackLabel: FEEDBACK_LABELS[b.report?.feedback] || '',
  feedbackAtLabel: b.report?.feedback_at
    ? fmtDateTime(b.report.feedback_at)
    : '',
  notes: b.report?.notes || '',
  photoUrl: b.report?.photo_url || '',
  // Whether the notes + photo have gone to the parent on WhatsApp (15 min after
  // the teacher saves both). Not the message itself; that is in the inbox.
  // Measured from the session's scheduled END (owner, 2026-10-22): how long
  // the parent waited after class for the notes + photo.
  summaryLabel: b.report?.summary_sent_at
    ? `Sent · ${fmtDateTime(b.report.summary_sent_at)}${afterEnd(
        b.report.summary_sent_at,
        b.date,
        b.endTime
      )}`
    : b.report?.summary_due_at
    ? `Scheduled · ${fmtDateTime(b.report.summary_due_at)}${afterEnd(
        b.report.summary_due_at,
        b.date,
        b.endTime
      )}`
    : '—'
})

// One grade per slot; listings carry several. Mirrors oh_at_home.constants.GRADES.
// At Home runs classes 1 to 8 only (owner, 2026-10-19: Nursery, LKG, UKG, 9 and
// 10 were dropped and the listings were pruned on dev and prod). 'Any' is
// unchanged and still means "open to every grade" — what changed is what every
// covers, so it now resolves to 1-8.
// Mirrors oh_at_home.constants.GRADES, including the order: UKG first, so the
// multi-select and the Grade column both read "UKG,1,2,3,4,5,6,7,8".
export const GRADES = ['UKG', '1', '2', '3', '4', '5', '6', '7', '8']

// The range in Alfred's own words, in one place so the sentences cannot drift
// from GRADES (owner, 2026-10-19).
export const GRADE_RANGE_LABEL = 'classes 1–8'
export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]

// Booked / Available / Inactive without the date: the word the Availability
// dropdown offers, so "Booked" picks every booked listing whatever its date.
const availability = (l) =>
  !l.active ? 'Inactive' : l.busyUntil ? 'Booked' : 'Available'

// Flatten a weekly listing (admin/listings) into table cells.
export const listingRow = (l) => ({
  ...l,
  timeLabel: `${fmtTime(l.start_time)} · ${l.duration_minutes} min`,
  gradesLabel: (l.grades || []).join(', '),
  availabilityLabel:
    availability(l) === 'Booked'
      ? `Booked until ${fmtDate(l.busyUntil)}`
      : availability(l),
  // A cell that joins several values filters on each of them on its own:
  // AtHomeCrudPage reads `<column>Filters` for the dropdown and keeps a row
  // when its tokens include the pick (owner, 2026-10-19).
  gradesLabelFilters: l.grades || [],
  availabilityLabelFilters: [availability(l)],
  bookedBy: l.busyChild || ''
})

// Flatten a module plan (admin/plans) into table cells.
export const planRow = (p) => ({
  ...p,
  childName: p.childInfo?.name || '',
  parentName: p.parentInfo?.name || '',
  parentPhone: p.parentInfo?.phone || '',
  parentUuid: p.parentInfo?.uuid || '',
  // The grade picked when the plan was booked; it is kept on the plan.
  childGradeLabel: p.grade || '—',
  schoolName: p.childInfo?.schoolName || '—',
  ...bookedCells(p.created),
  teacherLabel: (p.teacherNames || []).join(', '),
  scheduleLabel: (p.listingsInfo || [])
    .map((l) => `${l.weekday.slice(0, 3)} ${fmtTime(l.start_time)}`)
    .join(' · '),
  moduleLabel: p.kind === 'monthly' ? 'Monthly classes' : p.moduleName || '—',
  kindLabel: p.kind === 'monthly' ? 'Monthly' : 'Projects',
  frequencyLabel:
    p.kind === 'monthly'
      ? `${p.frequency === 1 ? 'Once' : 'Twice'} a week · one month`
      : `${p.frequency === 1 ? 'Once' : 'Twice'} a week · ${
          p.weeks || (p.frequency === 1 ? 8 : 4)
        } weeks`,
  datesLabel: `${fmtDate(p.start_date)} – ${fmtDate(p.end_date)}`,
  priceLabel: fmtMoney(p.price),
  paymentLabel:
    p.payment_status === 'link_sent'
      ? 'Link sent · nothing booked until paid'
      : titleCase(p.payment_status),
  // Only while it can still be paid: the URL stays on the plan after a payment
  // or a cancel, when opening it would show a dead page.
  payLinkUrl:
    p.payment_status === 'link_sent' && p.status !== 'cancelled'
      ? p.payment_link_url || ''
      : '',
  statusLabel: titleCase(p.status),
  progressLabel: p.progress
    ? `${p.progress.completed}/${p.progress.total} done`
    : ''
})

export const CATEGORY_STATUSES = [
  { value: 'open', label: 'Open — parents can book' },
  { value: 'upcoming', label: 'Upcoming — shown as coming soon' },
  { value: 'inactive', label: 'Inactive — discontinued, hidden' }
]
// "Inactive — hidden" was NOT true and is corrected here (2026-10-19). A
// category is genuinely hidden when inactive -- `parent.py` excludes it -- but
// the modules endpoint applies no status filter at all, and the app draws
// anything that is not `open` with the same "Coming soon" stamp
// (ProjectsRow.tsx:105, `soon: row.status !== 'open'`). So choosing Inactive
// to take a project out of the app did not take it out of the app. Nothing on
// prod is inactive today, so this is a label telling the truth rather than a
// fix to live data; making the endpoint behave the way the old label claimed
// is a separate, parent-facing change.
export const MODULE_STATUSES = [
  { value: 'open', label: 'Open — bookable' },
  { value: 'coming_soon', label: 'Coming soon — shown, not bookable' },
  { value: 'inactive', label: 'Inactive — retired, still shown' }
]
export const statusLabel = (list, v) =>
  (list.find((o) => o.value === v)?.label || titleCase(v || '')).split(' — ')[0]

// Frequency wording used everywhere a module is described. A project runs for
// its own session count, so `sessions` is that project's `sessions_count`; with
// no project to read it from (none picked yet, the per-category Pricing table)
// only the frequency is stated. Weeks round up like the backend's weeks_for:
// 7 sessions twice a week is 4 weeks.
export const freqLabel = (f, style = 'module', sessions) => {
  const freq = Number(f) === 1 ? 'Once a week' : 'Twice a week'
  if (style === 'regular') return `${freq} · booked month by month`
  const count = Number(sessions)
  if (!count) return freq
  const weeks = Math.ceil(count / Number(f))
  return `${freq} · ${count} session${
    count === 1 ? '' : 's'
  } over ${weeks} week${weeks === 1 ? '' : 's'}`
}

// How a category reads in every At-Home dropdown: "STEAM · Projects", "Chess · Monthly".
export const categoryLabel = (c) =>
  `${c.name} · ${c.style === 'regular' ? 'Monthly' : 'Projects'}`

// The server's own sentence for a refused request, for showing inside a dialog.
export const apiErrorMessage = (error, fallback = 'Something went wrong.') => {
  const d = error?.response?.data
  if (!d) return error?.message || fallback
  if (Array.isArray(d)) return d.join(' ')
  if (typeof d.error === 'string') return d.error
  if (typeof d.detail === 'string') return d.detail
  if (typeof d.message === 'string') return d.message
  if (Array.isArray(d.non_field_errors)) return d.non_field_errors.join(' ')
  return fallback
}

// A child whose school standard is one At Home does not run. Real data on prod
// (an LKG child, a class 9 child), so Alfred has to answer for them rather than
// crash or wave them through (owner, 2026-10-19).
//
// Two shapes arrive from the API and both mean the same thing: no `grade` at
// all with the school standard in `standardName` (the backend's
// grade_for_student found no At Home equivalent), and a `grade` that is simply
// not one of GRADES (the backend's standard-to-grade map still knows the old
// vocabulary, so 'LKG' still comes back on the wire). Judging against GRADES
// rather than trusting the field covers both.
export const isOutsideGradeRange = (child) =>
  Boolean(child) &&
  (child.grade ? !GRADES.includes(child.grade) : Boolean(child.standardName))

// A readable standard for such a child: their school's own word for it when we
// have it, else whatever grade came back.
export const outsideGradeName = (child) =>
  child?.standardName || (child?.grade ? `grade ${child.grade}` : '')

// Why a child cannot take a slot of `slotGrade`, in ops' words; '' when they can.
//
// The out-of-range test runs BEFORE the 'Any' wildcard: 'Any' means every grade
// At Home runs, and since 2026-10-19 that is 1-8, so a dropped-standard child
// is not bookable on an 'Any' slot either. A child with no standard on file at
// all is a different case and keeps its old answer — they stay bookable on an
// 'Any' slot, which is what the server's own gate does.
export const gradeMismatch = (child, slotGrade) => {
  if (isOutsideGradeRange(child))
    return `in ${outsideGradeName(child)}, outside ${GRADE_RANGE_LABEL}`
  if (!slotGrade || slotGrade === 'Any') return ''
  if (!child.grade) return 'no grade on file'
  if (child.grade === slotGrade) return ''
  return `in grade ${child.grade}, this slot is grade ${slotGrade}`
}
